import { CompetitionType, Season, SeasonLeague } from '@models';

/**
 * Comparador de orden de visualización de temporadas, compartido por la app
 * (listado de rankings y chips del home de federación) para no duplicar el
 * criterio.
 *
 * Criterio, en este orden:
 *
 * 1. Las temporadas con `order` definido (número finito, **el 0 incluido**) van
 *    primero, ordenadas por `order` ascendente. `order` es el orden manual que
 *    fija el admin en la competición y que se denormaliza en la temporada.
 * 2. Las que no lo tienen van después, con el criterio histórico: las ligas por
 *    `level` ascendente con `name.localeCompare` de desempate, y el resto
 *    (copas) por `name.localeCompare`.
 * 3. Dos temporadas con el mismo `order` se desempatan con ese mismo criterio
 *    histórico.
 *
 * No agrupa por tipo: se asume que quien llama ya ha agrupado (liga / copa) y
 * usa este comparador **dentro** de cada grupo.
 */
export function compareSeasonsByDisplayOrder(a: Season, b: Season): number {
  const orderA = hasDisplayOrder(a) ? a.order! : null;
  const orderB = hasDisplayOrder(b) ? b.order! : null;

  if (orderA !== null && orderB !== null) {
    if (orderA !== orderB) return orderA - orderB;
  } else if (orderA !== null) {
    return -1;
  } else if (orderB !== null) {
    return 1;
  }

  return compareSeasonsLegacy(a, b);
}

/** `order` válido: número finito, incluido el 0. */
function hasDisplayOrder(season: Season): boolean {
  return typeof season.order === 'number' && Number.isFinite(season.order);
}

/**
 * Criterio histórico, previo al orden manual: las ligas por `level` ascendente
 * (con el nombre de desempate) y el resto por nombre.
 */
function compareSeasonsLegacy(a: Season, b: Season): number {
  if (a.type === CompetitionType.LEAGUE && b.type === CompetitionType.LEAGUE) {
    const levelDiff = ((a as SeasonLeague).level ?? 0) - ((b as SeasonLeague).level ?? 0);
    if (levelDiff !== 0) return levelDiff;
  }
  return a.name.localeCompare(b.name);
}
